import React, { useState } from 'react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { createScreenGroup } from 'core/API/ScreenSetting';
import styles from './ScreenGroupForm.module.scss';
import screenStyles from './ScreenSetting.module.scss';
import { Button as MuiButton, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { checkAndAutoActivateScheduling } from 'core/API/gameMasterScheduling';

interface CreateScreenGroupSettingsProps {
    screenGroups: any[];
    handleBackClick: () => void;
    onScreenGroupCreated?: () => void;
}

const CreateScreenGroupSettings: React.FC<CreateScreenGroupSettingsProps> = ({
    screenGroups,
    handleBackClick,
    onScreenGroupCreated,
}) => {
    const { workplaceId } = useWorkplacesContext();
    const [label, setLabel] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const queryClient = useQueryClient();

    const isDuplicateLabel = screenGroups.some(
        (group: any) => group.label.toLowerCase() === label.toLowerCase()
    );

    const handleCreateScreenGroup = async () => {
        setError(null);
        setIsLoading(true);

        if (!label.trim()) {
            setError('Please enter a screen group label.');
            setIsLoading(false);
            return;
        }
        if (isDuplicateLabel) {
            setError('This screen group label already exists.');
            setIsLoading(false);
            return;
        }

        try {
            const workplaceIdValue = workplaceId || Number(localStorage.getItem("workplace-id"));
            await createScreenGroup({
                label: label.trim(),
                workplace: { id: workplaceIdValue },
                list_order: screenGroups.length + 1,
                deleted: false,
            });
            await queryClient.invalidateQueries(['screenGroups', workplaceIdValue]);
            await queryClient.invalidateQueries(['screens']);
            await queryClient.invalidateQueries(['gamemaster-planner', workplaceIdValue]);
            try {
                await checkAndAutoActivateScheduling(workplaceIdValue);
            } catch (e) {
                console.warn('Auto-activate scheduling check failed:', e);
            }
            await queryClient.invalidateQueries(['gameMasterSettings', workplaceIdValue]);
            onScreenGroupCreated?.();
            handleBackClick();
        } catch (error: any) {
            console.error('Error creating screen group:', error);
            setError(error?.response?.data?.message || error?.message || 'Failed to create screen group. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const isFormValid = label.trim() && !isDuplicateLabel;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleCreateScreenGroup();
    };

    return (
        <div className={screenStyles.createScreenGroupSetting}>
            <div className={screenStyles.formContainer}>
                <Grid container direction="column" spacing={2} pl={2}>
                    <Grid container direction="row" justifyContent="space-between">
                        <Grid>
                            <MuiButton className={screenStyles.backButton} onClick={handleBackClick}>
                                Back
                            </MuiButton>
                        </Grid>
                        <Grid item xs={6} display={'flex'} justifyContent="flex-end">
                            <Grid>
                                <MuiButton
                                    className={screenStyles.saveButton}
                                    onClick={handleSubmit}
                                    disabled={isLoading || !isFormValid}
                                >
                                    {isLoading ? 'Saving...' : 'Save'}
                                </MuiButton>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
                        <h2 className={screenStyles.formTitle}>Create Screen Group</h2>
                    </Grid>
                </Grid>

                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className={screenStyles.errorMessage}>
                            {error}
                        </div>
                    )}

                    <div className={styles.formRow}>
                        <div className={styles.formField}>
                            <label className={styles.label}>Screen Group</label>
                            <Input
                                type="text"
                                required
                                placeholder="Screen Group"
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                className={styles.input}
                            />
                            {isDuplicateLabel && (
                                <span className={styles.fieldError}>This screen group label already exists.</span>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateScreenGroupSettings;

